import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Building2,
  Key,
  Wrench,
  Paintbrush,
  Hammer,
  Droplets,
  Zap,
  Clock,
  Shield,
  Users,
  Home as HomeIcon
} from 'lucide-react';
import { companyInfo } from '../data/mock';
const companyPhone = '07300 405045';
const LandlordServices = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    properties: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.properties,
          job_description: `[Landlord/Agent Enquiry] ${formData.message}`,
          trade_type: 'general',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/landlord-services'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', properties: '', message: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const services = [
    {
      icon: Wrench,
      title: 'Reactive Maintenance',
      description: 'Fast response to urgent repairs, tenant-reported issues and property emergencies. One call to resolve any maintenance problem.'
    },
    {
      icon: Clock,
      title: 'Planned Maintenance',
      description: 'Scheduled maintenance programmes to keep your properties in excellent condition and prevent costly emergency repairs.'
    },
    {
      icon: Key,
      title: 'End of Tenancy Repairs',
      description: 'Quick turnaround repairs between tenancies. Damaged walls, doors, fixtures, decorating and general wear-and-tear repairs.'
    },
    {
      icon: HomeIcon,
      title: 'Void Property Works',
      description: 'Complete void turnaround including repairs, redecoration, flooring and preparation for new tenants.'
    },
    {
      icon: Paintbrush,
      title: 'Decorating',
      description: 'Professional painting and decorating to refresh tired properties. Interior and exterior work to a high standard.'
    },
    {
      icon: Hammer,
      title: 'Carpentry',
      description: 'Door repairs, skirting boards, architraves, built-in storage, shelving and all general joinery work.'
    },
    {
      icon: Building2,
      title: 'Plaster Repairs',
      description: 'Patch repairs, hole filling, crack repairs, skimming and full re-plasters. Wall preparation before decorating.'
    },
    {
      icon: Droplets,
      title: 'Minor Plumbing',
      description: 'Leaking taps, toilet repairs, radiator issues, shower problems and general plumbing maintenance.'
    },
    {
      icon: Zap,
      title: 'Minor Electrical',
      description: 'Socket and switch replacements, light fitting installations, extractor fans and minor electrical repairs.'
    },
    {
      icon: Building2,
      title: 'Property Refurbishment',
      description: 'Complete rental property refurbishments to increase rental value and attract quality tenants.'
    }
  ];
  const benefits = [
    'Single point of contact for all maintenance',
    'Multi-trade capability — no need to coordinate multiple contractors',
    'Understanding of rental property requirements',
    'Fast turnaround to minimise void periods',
    '£5 million public liability insurance',
    '12-month workmanship guarantee',
    'Covering Greater Manchester and Cheshire'
  ];
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Property Maintenance for Landlords | Rental Property Repairs | Expert Property Maintenance</title>
        <meta name="description" content="Property maintenance services for landlords in Manchester. Reactive repairs, void works, end of tenancy, refurbishments. One contact for all trades. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/landlord-services" />
      </Helmet>
      <Header variant="default" companyPhone={companyPhone} />
      {/* Hero - compact on mobile */}
      <section className="pt-10 pb-8 sm:pt-20 sm:pb-14 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-sm font-medium mb-4 sm:mb-6">
              <Building2 className="w-4 h-4" />
              For Landlords & Letting Agents
            </div>
            <h1 className="text-[28px] leading-[1.15] sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-6">
              Property Maintenance for{' '}
              <span className="text-brand-yellow">Landlords & Letting Agents</span>
            </h1>
            <p className="text-[16px] leading-relaxed sm:text-lg text-slate-300 mb-5 sm:mb-8">
              Expert Property Maintenance provides reliable maintenance and repair services for landlords, 
              letting agents and property managers across Greater Manchester. One contact for all your 
              property maintenance needs.
            </p>
            <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold text-[16px] h-12 sm:h-14 w-full sm:w-auto" asChild>
                <a href="#contact">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy text-[16px] h-12 sm:h-14 w-full sm:w-auto" asChild>
                <a href={`tel:${companyPhone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyPhone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Why Property Professionals Choose Us */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Why Property Professionals Choose Expert Property Maintenance
              </h2>
              <p className="text-lg text-brand-slate mb-8">
                Managing rental properties means dealing with a constant stream of maintenance issues. 
                Expert Property Maintenance gives you a single, reliable point of contact for all property works — 
                from small repairs to complete refurbishments.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-xl font-bold text-brand-navy mb-6">How We Support Your Properties</h3>
              <div className="space-y-4">
                <div className="p-4 bg-brand-grey rounded-xl">
                  <h4 className="font-semibold text-brand-navy mb-1">Individual Repair Jobs</h4>
                  <p className="text-sm text-brand-slate">One-off repairs and maintenance tasks across your portfolio.</p>
                </div>
                <div className="p-4 bg-brand-grey rounded-xl">
                  <h4 className="font-semibold text-brand-navy mb-1">Ongoing Maintenance</h4>
                  <p className="text-sm text-brand-slate">Regular maintenance support for landlords managing multiple properties.</p>
                </div>
                <div className="p-4 bg-brand-grey rounded-xl">
                  <h4 className="font-semibold text-brand-navy mb-1">Tenant-Reported Problems</h4>
                  <p className="text-sm text-brand-slate">Prompt response to issues reported by tenants, keeping you informed throughout.</p>
                </div>
                <div className="p-4 bg-brand-grey rounded-xl">
                  <h4 className="font-semibold text-brand-navy mb-1">Complete Refurbishments</h4>
                  <p className="text-sm text-brand-slate">Full rental property refurbishments to maximise rental potential.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Landlord & Letting Agent Services
            </h2>
            <p className="text-lg text-brand-slate max-w-2xl mx-auto">
              From reactive repairs to planned maintenance and complete refurbishments — 
              all trades under one roof.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card key={service.title} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-brand-grey rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-yellow transition-colors">
                    <service.icon className="w-6 h-6 text-brand-yellow group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{service.title}</h3>
                  <p className="text-brand-slate text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            One Contact for All Your Property Maintenance
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Stop coordinating multiple contractors. Expert Property Maintenance handles carpentry, 
            painting, plastering, plumbing, electrical and general repairs — all through one point of contact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
              <a href="#contact">
                Request a Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
              <a href={`tel:${companyPhone}`}>
                <Phone className="w-5 h-5 mr-2" />
                {companyPhone}
              </a>
            </Button>
          </div>
        </div>
      </section>
      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Discuss Your Properties</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about your property portfolio and maintenance requirements. 
                We will get back to you within 24 hours.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyPhone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyPhone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Service area</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-slate-800 rounded-xl">
                <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
                <div className="text-white font-medium">£5 million public liability insurance</div>
                <div className="text-white font-medium">12-month workmanship guarantee</div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Get in Touch</h3>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@company.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Number of Properties (approx)</label>
                      <Input value={formData.properties} onChange={(e) => setFormData({...formData, properties: e.target.value})} placeholder="e.g., 5 properties in Stockport" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">How can we help? *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your maintenance requirements..." rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance - Landlord Services Manchester</p>
            <div className="flex gap-6">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/property-refurbishment" className="hover:text-brand-yellow">Refurbishment</a>
              <a href="/end-of-tenancy" className="hover:text-brand-yellow">End of Tenancy</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyPhone} />
    </div>
  );
};
export default LandlordServices;
